import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CreditCard, Landmark, ShieldCheck } from 'lucide-react';
import { usePaymentSettings, useUpdatePaymentSettings } from '@/hooks/usePayments';
import { useCompetition } from '@/hooks/useCompetitions';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, ErrorState } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { getApiErrorMessage } from '@/api/client';
import { centsToEuroText, parseEuroToCents } from '@/lib/payments';
import type { PaymentSettings } from '@/api/payments';

function Toggle({ label, hint, checked, disabled, onChange }: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className={`flex items-start gap-3 text-sm ${disabled ? 'opacity-60' : ''}`}>
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-accent"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-medium text-fg">{label}</span>
        {hint && <span className="block text-fg-muted">{hint}</span>}
      </span>
    </label>
  );
}

function PaymentsForm({ settings }: { settings: PaymentSettings }) {
  const update = useUpdatePaymentSettings(settings.competitionId);

  const [fee, setFee] = useState(centsToEuroText(settings.entryFeeCents));
  const [recipient, setRecipient] = useState(settings.recipientName ?? '');
  const [iban, setIban] = useState(settings.iban ?? '');
  const [street, setStreet] = useState(settings.street ?? '');
  const [city, setCity] = useState(settings.city ?? '');
  const [bankQr, setBankQr] = useState(settings.bankTransferQrEnabled);
  const [stripeOn, setStripeOn] = useState(settings.stripeEnabled);
  // Empty = keep the saved key. The saved key is never shown, only its last characters.
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saved, setSaved] = useState(false);
  const [feeError, setFeeError] = useState<string | undefined>();

  async function submit(e: FormEvent, removeStripeKeys = false) {
    e.preventDefault();
    setSaved(false);
    const cents = parseEuroToCents(fee);
    if (Number.isNaN(cents)) {
      setFeeError('Vnesite znesek, npr. 15 ali 12,50.');
      return;
    }
    setFeeError(undefined);
    await update.mutateAsync({
      entryFeeCents: cents,
      recipientName: recipient.trim() || null,
      iban: iban.trim() || null,
      street: street.trim() || null,
      city: city.trim() || null,
      bankTransferQrEnabled: bankQr,
      stripeEnabled: stripeOn,
      stripeSecretKey: secretKey.trim() || null,
      stripeWebhookSecret: webhookSecret.trim() || null,
      removeStripeKeys,
    });
    // The typed secrets leave the page as soon as they are saved.
    setSecretKey('');
    setWebhookSecret('');
    if (removeStripeKeys) setStripeOn(false);
    setSaved(true);
  }

  const keysReady = settings.stripeKeySet && settings.stripeWebhookSecretSet;

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" onChange={() => setSaved(false)} autoComplete="off">
      {update.isError && <Alert variant="error">{getApiErrorMessage(update.error)}</Alert>}
      {saved && <Alert variant="success">Nastavitve plačil so shranjene.</Alert>}

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">Kotizacija</h2>
          <p className="text-sm text-fg-muted">
            Če kotizacije ne vpišete, plačila nikjer niso vidna. Znesek velja za to tekmovanje.
          </p>
        </CardHeader>
        <CardBody>
          <div className="max-w-xs">
            <Input
              label="Znesek v evrih"
              inputMode="decimal"
              placeholder="npr. 15,00"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              error={feeError}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-fg">
            <Landmark size={16} aria-hidden="true" /> Bančno nakazilo (UPN QR)
          </h2>
          <p className="text-sm text-fg-muted">
            Podatki o klubu veljajo za vsa vaša tekmovanja, zato jih vnesete samo enkrat. Tekmovalec dobi QR kodo, ki jo prebere
            aplikacija njegove banke. Plačilo nato označite sami v seznamu tekmovalcev.
          </p>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <Toggle label="Pokaži QR kodo za nakazilo" checked={bankQr} onChange={setBankQr} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Prejemnik" value={recipient} maxLength={33} onChange={(e) => setRecipient(e.target.value)} />
            <Input label="IBAN" value={iban} maxLength={40} placeholder="SI56 …" onChange={(e) => setIban(e.target.value)} />
            <Input label="Naslov prejemnika" value={street} maxLength={33} onChange={(e) => setStreet(e.target.value)} />
            <Input label="Poštna številka in kraj" value={city} maxLength={33} onChange={(e) => setCity(e.target.value)} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-fg">
            <CreditCard size={16} aria-hidden="true" /> Plačilo s kartico (Stripe)
          </h2>
          <p className="text-sm text-fg-muted">
            Tekmovalec plača na Stripe strani, plačilo se samodejno označi kot plačano. Potrebujete svoj račun pri Stripe; postopek
            je v navodilih za organizatorje.
          </p>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {!settings.secretStorageAvailable && (
            <Alert variant="warning">
              Strežnik nima nastavljenega ključa za šifriranje (PAYMENTS_ENCRYPTION_KEY), zato Stripe ključev ni mogoče shraniti.
              Prosite administratorja.
            </Alert>
          )}
          <p className="flex items-start gap-2 text-sm text-fg-secondary">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            Ključi se pred shranjevanjem šifrirajo (AES-256), se nikoli ne prikažejo nazaj in jih ne vidi nihče, niti administratorji.
          </p>

          <Toggle
            label="Omogoči plačilo s kartico"
            hint={keysReady ? undefined : 'Najprej shranite ključ in podpisno skrivnost spodaj.'}
            checked={stripeOn}
            disabled={!keysReady && !(secretKey.trim() && webhookSecret.trim())}
            onChange={setStripeOn}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Stripe skrivni ali omejeni ključ (sk_… / rk_…)"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={secretKey}
              placeholder={settings.stripeKeySet ? `Shranjen (${settings.stripeKeyHint}), vpišite za zamenjavo` : 'rk_live_…'}
              onChange={(e) => setSecretKey(e.target.value)}
              disabled={!settings.secretStorageAvailable}
            />
            <Input
              label="Podpisna skrivnost webhooka (whsec_…)"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={webhookSecret}
              placeholder={settings.stripeWebhookSecretSet ? 'Shranjena, vpišite za zamenjavo' : 'whsec_…'}
              onChange={(e) => setWebhookSecret(e.target.value)}
              disabled={!settings.secretStorageAvailable}
            />
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-fg">Naslov webhooka (vpišete ga v Stripe)</span>
            <code className="break-all rounded-control bg-surface-2 px-2 py-1 text-fg-secondary">{settings.stripeWebhookUrl}</code>
          </div>

          {settings.stripeKeySet && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="self-start"
              isLoading={update.isPending}
              onClick={(e) => {
                if (confirm('Izbrišem shranjena Stripe ključa? Plačilo s kartico bo izklopljeno.')) void submit(e, true);
              }}
            >
              Izbriši shranjena ključa
            </Button>
          )}
        </CardBody>
      </Card>

      <div>
        <Button type="submit" isLoading={update.isPending}>
          Shrani
        </Button>
      </div>
    </form>
  );
}

/** Per-competition payment setup. Club details and Stripe keys are the organization's and are reused by its other competitions. */
export function OrganizerPaymentsPage() {
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const competition = useCompetition(competitionId);
  const settings = usePaymentSettings(competitionId);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <Link to="/organizer/competitions" className="text-sm text-fg-muted hover:underline">
          &larr; Nazaj na tekmovanja
        </Link>
        <h1 className="text-xl font-semibold text-fg">Plačila {competition.data ? `za "${competition.data.name}"` : ''}</h1>
        <p className="text-sm text-fg-muted">
          Neobvezno: kotizacija, QR koda za bančno nakazilo in plačilo s kartico. Dokler kotizacije ne vpišete, tekmovalci ničesar ne vidijo.{' '}
          <Link to="/navodila-za-uporabo/organizator" className="font-medium text-accent-text underline">
            Navodila za nastavitev Stripe
          </Link>
        </p>
      </div>

      {settings.isLoading && <PageSpinner />}
      {settings.isError && <ErrorState message={getApiErrorMessage(settings.error)} onRetry={() => settings.refetch()} />}
      {settings.data && <PaymentsForm settings={settings.data} />}
    </div>
  );
}
